import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

import type { AnchorRock } from "@/api/drives";
import { PersonPicker, type Profile } from "@/components/people/person-picker";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useBrands } from "@/hooks/use-brands";
import { useCapabilities } from "@/hooks/use-capabilities";
import { useCreateGoalDrive, usePushRocks } from "@/hooks/use-drives";

import { PUSH_KPIS, type PushKpi, defaultBrands, pushRequest } from "./push-rock-model";

/** "Push this rock": shown only when the caller may start a revenue-push Drive from this rock. */
export function PushRockAction({ rockId }: { rockId: string }) {
  const { data: rocks } = usePushRocks();
  const [open, setOpen] = useState(false);
  const rock = rocks?.find((r) => r.id === rockId);
  if (!rock) return null;
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="push-rock">
        <TrendingUp className="mr-1.5 h-3.5 w-3.5" />
        Push this rock
      </Button>
      {open && <PushRockDialog rock={rock} onOpenChange={setOpen} />}
    </>
  );
}

function PushRockDialog({ rock, onOpenChange }: { rock: AnchorRock; onOpenChange: (open: boolean) => void }) {
  const { can } = useCapabilities();
  const leader = can("drives.revenue_push");
  const { data: brandList = [] } = useBrands(leader);
  const [kpi, setKpi] = useState<PushKpi>("sales.revenue");
  const [target, setTarget] = useState(rock.target ? Math.round(rock.target).toLocaleString("en-US") : "");
  const [picked, setPicked] = useState<string[] | null>(null);
  const [recipients, setRecipients] = useState<Profile[]>([]);
  const [search, setSearch] = useState("");
  const create = useCreateGoalDrive();
  const navigate = useNavigate();

  const brands = picked ?? defaultBrands(rock, brandList);
  const amount = Number(target.replace(/[$,\s]/g, ""));
  const ready = amount > 0 && (!leader || brands.length > 0);

  const start = () =>
    create.mutate(
      pushRequest(rock, {
        kpi: leader ? kpi : "sales.revenue",
        target: amount,
        // The server sets a rep's brand to their own.
        brands: leader ? brands : [rock.tenant_id ?? ""],
        recipients: recipients.map((p) => p.id),
      }),
      {
        onSuccess: (drive) => {
          toast.success(`${drive.name} started`);
          navigate(`/drives/${drive.id}`);
        },
        onError: (err: Error) => toast.error(err.message || "Couldn't start that Drive"),
      },
    );

  const toggleBrand = (id: string) =>
    setPicked(brands.includes(id) ? brands.filter((b) => b !== id) : [...brands, id]);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Push this rock</DialogTitle>
          <DialogDescription>
            Each weekday morning, Kory paces {rock.title} quarter to date and names the deals to push.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-5">
          {leader && (
            <div className="grid gap-2">
              <Label>What it measures</Label>
              <RadioGroup value={kpi} onValueChange={(v) => setKpi(v as PushKpi)} className="flex gap-4">
                {PUSH_KPIS.map((k) => (
                  <label key={k.slug} className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value={k.slug} />
                    {k.name}
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="push-target">Quarter target</Label>
            <Input
              id="push-target"
              inputMode="numeric"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="2,800,000"
            />
            <p className="text-xs text-muted-foreground">
              {rock.target ? "Read from the rock title. Check it before you start." : "The rock title names no amount."}
            </p>
          </div>
          {leader && (
            <div className="grid gap-2">
              <Label>Brands</Label>
              <div className="grid grid-cols-2 gap-2">
                {brandList.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={brands.includes(b.id)} onCheckedChange={() => toggleBrand(b.id)} />
                    {b.short_name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {leader && (
            <div className="grid gap-2">
              <Label>Also show it to</Label>
              <PersonPicker
                selected={recipients}
                onToggle={(p) =>
                  setRecipients((cur) => (cur.some((x) => x.id === p.id) ? cur.filter((x) => x.id !== p.id) : [...cur, p]))
                }
                searchQuery={search}
                onSearchChange={setSearch}
                listClassName="max-h-48"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={start} disabled={!ready || create.isPending} aria-busy={create.isPending}>
            {create.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {create.isPending ? "Starting…" : "Start the Drive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
